import styled from 'styled-components';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';

const STYLES = [
  { id: 'watercolor', label: 'Watercolor', icon: '🎨', prompt: 'Transform this into a beautiful watercolor painting with soft washes, flowing colors, and delicate wet-on-wet effects' },
  { id: 'oil', label: 'Oil Painting', icon: '🖼️', prompt: 'Convert this into a rich oil painting with visible brushstrokes, deep saturated colors, and textured impasto technique' },
  { id: 'impressionist', label: 'Impressionist', icon: '🌸', prompt: 'Reimagine this in the style of French Impressionism with dappled light, loose brushwork, and vibrant color harmonies' },
  { id: 'pop_art', label: 'Pop Art', icon: '💥', prompt: 'Transform this into bold pop art with bright contrasting colors, graphic outlines, and halftone dot patterns' },
  { id: 'pencil_sketch', label: 'Pencil Sketch', icon: '✏️', prompt: 'Convert this into a detailed pencil sketch with careful shading, cross-hatching, and fine linework on white paper' },
  { id: 'anime', label: 'Anime', icon: '🌟', prompt: 'Transform this into anime/manga art style with clean lines, vibrant colors, expressive eyes, and cel-shading' },
  { id: 'pixel_art', label: 'Pixel Art', icon: '👾', prompt: 'Convert this into retro pixel art with a limited color palette, visible pixels, and a nostalgic 16-bit video game aesthetic' },
  { id: 'stained_glass', label: 'Stained Glass', icon: '🏛️', prompt: 'Transform this into a stained glass window design with bold black lead lines, translucent jewel-toned colors, and geometric segments' },
];

export default function StyleTransferModal({ imageUrl, onSelect, onClose }) {
  return (
    <ModalOverlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <ModalContent
        onClick={(e) => e.stopPropagation()}
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      >
        <ModalHeader>
          <ModalTitle>Transform Style</ModalTitle>
          <CloseBtn onClick={onClose}><X size={20} /></CloseBtn>
        </ModalHeader>

        <PreviewRow>
          <PreviewImg src={imageUrl} alt="" />
          <PreviewLabel>Select an art style to transform your artwork</PreviewLabel>
        </PreviewRow>

        <StyleGrid>
          {STYLES.map((style) => (
            <StyleCard key={style.id} onClick={() => onSelect(style)}>
              <StyleIcon>{style.icon}</StyleIcon>
              <StyleName>{style.label}</StyleName>
            </StyleCard>
          ))}
        </StyleGrid>
      </ModalContent>
    </ModalOverlay>
  );
}

const ModalOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 500;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const ModalContent = styled(motion.div)`
  width: 100%;
  max-width: 480px;
  max-height: 85vh;
  background: ${({ theme }) => theme.colors.background};
  border-radius: 20px 20px 0 0;
  padding: 20px;
  padding-bottom: max(20px, env(safe-area-inset-bottom));
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
`;

const CloseBtn = styled.button`
  padding: 8px;
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.textSecondary};

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
  }
`;

const PreviewRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding: 12px;
  background: ${({ theme }) => theme.colors.surfaceSecondary};
  border-radius: ${({ theme }) => theme.radius.medium};
`;

const PreviewImg = styled.img`
  width: 56px;
  height: 56px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
`;

const PreviewLabel = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.4;
`;

const StyleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;

  @media (max-width: 360px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const StyleCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 14px 8px;
  border-radius: ${({ theme }) => theme.radius.medium};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  transition: all 0.15s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.accent};
    background: ${({ theme }) => theme.colors.surfaceSecondary};
    transform: translateY(-1px);
  }

  &:active {
    transform: scale(0.97);
  }
`;

const StyleIcon = styled.span`
  font-size: 28px;
`;

const StyleName = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  text-align: center;
  line-height: 1.2;
`;
